import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

interface Props {
  label: string
  /** The answer. Rendered larger and darker than the question, always. */
  children: ReactNode
  /** A quieter line under the value, where it needs one. */
  hint?: string | undefined
}

/**
 * A label and the value it names.
 *
 * The order matters and so does the weight. Several screens had this the wrong
 * way round — a bold caption over a body-sized value — so the eye landed on
 * the word «موجودی» rather than on the amount, and the page read as a form
 * somebody had forgotten to fill in.
 *
 * So the label is small, quiet and above; the value is the largest thing in
 * the block. Nothing here is clever: it is one rule, applied everywhere, which
 * is what makes a page look composed rather than assembled.
 */
export function Fact({ label, children, hint }: Props) {
  return (
    <Box>
      <Typography
        variant="caption"
        component="p"
        color="text.secondary"
        sx={{ mb: 0.25, lineHeight: 1.6 }}
      >
        {label}
      </Typography>
      <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
        {children}
      </Typography>
      {hint ? (
        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.25 }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  )
}
